import {
  AgentBehaviorType,
  AgentDataByIdType,
  AgentDataType,
  AgentParamsTypes,
  AgentResponseTypes,
  AgentSkillDetailType,
  AgentSkillsResponseType,
  AgentTemplateType,
  CreateAgentSkillParams,
  DeleteAgentSkillsParams,
  PostAgentConfigsType,
  PreviewWebUrlParamsTypes,
  ResponseAgentConfigsType,
  StorageAgentDataType,
  TrainWebUrlParamsTypes,
  TrainWebUrlResponseTypes,
  UpdateAgentParams,
  UpdateAgentSkillParams,
  DisplayCrawlCompletedType,
  UpdateAgentSkillPublishParams
} from './types';

import { ConversationResponseTypes, FeedbackConversationService } from 'services/accountConversations/types';
import axiosInstance from 'services/common/instance';

export const getAgentsService = async (
  params: RequestParamsTypes
): Promise<APIPagination<AgentDataType[]>> => {
  const res = await axiosInstance.get('agents', { params });
  return res.data;
};

export const getAgentByIdService = async (
  code: string
): Promise<AgentDataByIdType> => {
  const res = await axiosInstance.get(`agents/${code}`);
  return res.data.data;
};

export const createAgentService = async (
  params: AgentParamsTypes
): Promise<AgentResponseTypes> => {
  const res = await axiosInstance.post('agents', params);
  return res.data.data;
};

export const updateAgentService = async (
  code: string,
  params: UpdateAgentParams
): Promise<void> => {
  await axiosInstance.put(`agents/${code}`, params);
};

export const deleteAgentsService = async (code: string): Promise<void> => {
  await axiosInstance.delete(`agents/${code}`);
};

export const previewWebUrlAgentsService = async (
  code: string,
  params: PreviewWebUrlParamsTypes
): Promise<string[]> => {
  const res = await axiosInstance.post(
    `agents/${code}/data-sources/preview-web-url`,
    params
  );
  return res.data.data;
};

export const trainWebUrlAgentsService = async (
  code: string,
  params: TrainWebUrlParamsTypes
): Promise<TrainWebUrlResponseTypes[]> => {
  const res = await axiosInstance.post(
    `agents/${code}/data-sources/train-web-url`,
    params
  );
  return res.data.data;
};

export const updateAgentStatusService = async (
  code: string,
  status: number
): Promise<void> => {
  await axiosInstance.put(`agents/update-status/${code}`, { status });
};

export const getDisplayCrawlCompletedService = async (
  code: string
): Promise<DisplayCrawlCompletedType> => {
  const res = await axiosInstance.get(`agents/display-craw-complete/${code}`);
  return res.data.data;
};

export const putDisplayCrawlCompletedService = async (
  code: string,
  crawlCompleted: boolean
): Promise<void> => {
  const res = await axiosInstance.put(`agents/display-craw-complete/${code}`, {
    displayCrawComplete: crawlCompleted,
  });
  return res.data;
};

export const getAgentConfigsService = async (
  code: string
): Promise<ResponseAgentConfigsType> => {
  const res = await axiosInstance.get(`agents/${code}/configs`);
  return res.data.data;
};

export const postAgentConfigsService = async (
  code: string,
  params: PostAgentConfigsType
): Promise<void> => {
  const res = await axiosInstance.post(`agents/${code}/configs`, params);
  return res.data;
};

export const getAgentConfigsTemplateService = async (
  code: string
): Promise<AgentTemplateType> => {
  const res = await axiosInstance.get(`agents/${code}/configs/template`);
  return res.data.data;
};

export const postAgentConfigsTestMailService = async (
  code: string,
  params: { mail: string }
): Promise<any> => {
  const res = await axiosInstance.post(
    `agents/${code}/configs/test-send-mail`,
    params
  );
  return res.data;
};

export const postAgentConfigsTestSMSService = async (
  code: string,
  params: { phone: string }
): Promise<any> => {
  const res = await axiosInstance.post(
    `agents/${code}/configs/test-send-sms`,
    params
  );
  return res.data;
};

export const putUpdateGeneralKnowledgeService = async (
  code: string,
  params: { includeAi: boolean }
): Promise<any> => {
  const res = await axiosInstance.put(
    `agents/update-general-knowledge/${code}`,
    params
  );
  return res.data;
};

export const updateAgentIntructionService = async (
  code: string,
  params: UpdateAgentParams
): Promise<void> => {
  await axiosInstance.put(`agents/update-instruction/${code}`, params);
};

export const updateMultipleVisibleSourceAgentService = async (
  code: string,
  params: { displaySource: boolean; codes: string[] }
) => {
  const res = await axiosInstance.put(
    `/agents/${code}/data-sources/update-multi/display-source`,
    params
  );

  return res.data;
};

export const getStorageAgentsService = async (): Promise<StorageAgentDataType[]> => {
  const res = await axiosInstance.get('dashboard/storage-agent');
  return res.data.data;
};

export const agentFeedbackConversationService = async (
  code: string,
  conversationCode: string,
  itemCode: string,
  params: FeedbackConversationService
): Promise<ConversationResponseTypes> => {
  const res = await axiosInstance.post(`agents/${code}/conversations/${conversationCode}/items/${itemCode}/feedback`, params);
  return res.data.data;
};

export const agentT2PService = async ({
  code,
  conversationCode,
  itemCode
}: {
  code: string;
  conversationCode: string;
  itemCode: string;
}): Promise<{ link: string }> => {
  const res = await axiosInstance.post(
    `agents/${code}/conversations/${conversationCode}/answer-speech/${itemCode}`
  );
  return res.data.data;
};

export const updateBehaviorService = async ({
  code,
  data
}: { code: string; data: AgentBehaviorType }): Promise<{ link: string }> => {
  const res = await axiosInstance.put(`agents/update-behavior/${code}`, data);
  return res.data.data;
};
// export const feedbackConversationService = async (
//   conversationCode: string,
//   code: string,
//   params: FeedbackConversationService
// ): Promise<ConversationResponseTypes> => {
//   const res = await axiosChatbotInstance.post(
//     `account-conversations/${conversationCode}/items/${code}/feedback`,
//     params
//   );
//   return res.data.data;
// };

export const getAgentSkillsService = async (
  agentCode: string
): Promise<AgentSkillsResponseType> => {
  const res = await axiosInstance.get(`agents/${agentCode}/skills`);
  return res.data;
};

export const createAgentSkillService = async (
  agentCode: string,
  params: CreateAgentSkillParams
): Promise<void> => {
  await axiosInstance.post(`agents/${agentCode}/skills`, params);
};

export const getAgentSkillDetailService = async (
  agentCode: string,
  skillId: string
): Promise<AgentSkillDetailType> => {
  const res = await axiosInstance.get(`agents/${agentCode}/skills/${skillId}`);
  return res.data;
};

export const updateAgentSkillService = async (
  agentCode: string,
  skillId: string,
  params: UpdateAgentSkillParams
): Promise<void> => {
  await axiosInstance.put(`agents/${agentCode}/skills/${skillId}`, params);
};

export const deleteAgentSkillsService = async (
  agentCode: string,
  params: DeleteAgentSkillsParams
): Promise<void> => {
  await axiosInstance.delete(`agents/${agentCode}/skills`, { data: params });
};

export const updateAgentSkillPublishService = async (
  agentCode: string,
  params: UpdateAgentSkillPublishParams
): Promise<void> => {
  await axiosInstance.put(`agents/${agentCode}/skills/update/publish`, params);
};
